// src/pages/NewsPage.jsx
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Newspaper, Search, AlertCircle, ExternalLink } from 'lucide-react';
import { useNews } from '../hooks/useNews';
import { usePullToRefresh } from '../hooks/usePullToRefresh';

export default function NewsPage() {
  // --- State Management ---
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  const [failedImages, setFailedImages] = useState(new Set());

  // --- Data Fetching using Custom Hook ---
  const {
    news,
    loadingNews,
    newsError,
    fetchNews,
    loadMoreGeneralNews,
    hasMore,
  } = useNews();

  // --- Silent pull-to-refresh (mobile) ---
  const containerRef = usePullToRefresh(() => fetchNews(1));

  // --- Infinite Scroll Logic ---
  const loader = useRef(null);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  useEffect(() => {
    if (!loadingNews) {
      setIsFetchingMore(false);
    }
  }, [loadingNews]);

  useEffect(() => {
    const currentLoader = loader.current;
    if (!currentLoader) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          if (loadingNews || isFetchingMore || !hasMore) {
            return;
          }
          setIsFetchingMore(true);
          loadMoreGeneralNews();
        }
      },
      { rootMargin: '100px' }
    );

    observer.observe(currentLoader);
    return () => observer.disconnect();
  }, [news, loadingNews, hasMore, isFetchingMore, loadMoreGeneralNews]);

  const handleImageError = useCallback((url) => {
    setFailedImages((prev) => new Set(prev).add(url));
  }, []);

  const handleCalendarClick = () => {
    window.open('https://www.mql5.com/en/economic-calendar', '_blank', 'noopener,noreferrer');
  };

  // Filters news based on search term
  const filteredNews = news.filter((article) =>
    article.headline.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    // Page Background: Light slate / Dark gray
    <div
      ref={containerRef}
      className="no-scrollbar h-full bg-slate-50 dark:bg-gray-900 p-4 md:p-6 xl:max-w-none xl:px-10 xl:py-8 max-w-7xl mx-auto transition-colors duration-300 overflow-y-auto overflow-x-hidden"
    >
      {/* Sticky compact bar — hero title scrolls beneath it */}
      <header className="sticky top-0 z-10 -mx-4 md:-mx-6 xl:-mx-10 px-4 md:px-6 xl:px-10 pt-12 md:pt-3 pb-3 bg-slate-50/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800">
        <h1 className="text-lg font-bold text-gray-900 dark:text-white">News & Calendar</h1>
      </header>

      <div className="pt-8 md:pt-6 pb-6 md:pb-4 mb-4 md:mb-3 flex flex-col items-center">
          <h1 className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 dark:from-blue-400 dark:via-purple-400 dark:to-pink-500 mb-6 md:mb-4 tracking-tight">
            Market News & Calendar
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-center max-w-2xl text-xl md:text-lg leading-relaxed">
            Stay updated with market news and economic events that impact your trades
          </p>
      </div>

      <div className="space-y-8">
        {/* --- Search Section --- */}
        <section>
          <div className="relative">
            <Search size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search news..."
              className="w-full bg-white dark:bg-gray-800/60 text-gray-900 dark:text-white rounded-xl px-4 py-3 pl-12 focus:outline-none focus:ring-2 focus:ring-blue-500 text-base border border-gray-200 dark:border-gray-600/50 focus:border-blue-500/50 shadow-sm dark:shadow-none"
            />
          </div>
        </section>

        {/* --- Tab Navigation --- */}
        <section>
          {/* Tabs Container: White card in light mode */}
          <div className="bg-white dark:bg-gray-800/40 backdrop-blur-lg rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700/50 p-4 shadow-sm dark:shadow-none">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('general')}
                className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-colors flex items-center justify-center ${
                  activeTab === 'general' 
                    ? 'bg-blue-100 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400' 
                    : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                <Newspaper size={20} className="mr-2" />
                General News
              </button>
              <button
                onClick={handleCalendarClick}
                className="flex-1 px-4 py-3 rounded-xl text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 transition-colors flex items-center justify-center"
              >
                Economic Calendar
                <ExternalLink size={14} className="ml-2" />
              </button>
            </div>
          </div>
        </section>

        {/* --- Content Based on Active Tab --- */}
        {activeTab === 'general' && (
          <section>
            {/* News Card Container */}
            <div className="bg-white dark:bg-gray-800/40 backdrop-blur-lg rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700/50 p-6 shadow-sm dark:shadow-none">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-6">
                <Newspaper size={20} className="text-blue-500 dark:text-blue-400" />
                Latest Market News
              </h2>

              {newsError && (
                <div className="text-center py-8 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-lg">
                  <AlertCircle size={32} className="text-red-500 dark:text-red-400 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-red-600 dark:text-red-400 mb-2">Failed to Load News</h3>
                  <p className="text-gray-500 dark:text-gray-400">{newsError}</p>
                </div>
              )}

              {loadingNews && news.length === 0 && !newsError && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-gray-500 dark:text-gray-400">Loading latest news...</p>
                </div>
              )}

              {!newsError && !loadingNews && (
                <div className="space-y-3">
                  {filteredNews.length > 0 ? (
                    filteredNews.map((article) => (
                      <a 
                        key={article.url} 
                        href={article.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block bg-gray-50 dark:bg-gray-800 p-4 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-100 dark:border-transparent"
                      >
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-grow">
                            <h3 className="font-medium text-gray-900 dark:text-white text-sm leading-snug">{article.headline}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {article.source?.name || article.source} • {new Date(article.publishedAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          {article.urlToImage && !failedImages.has(article.urlToImage) && (
                            <img
                              src={article.urlToImage}
                              alt={article.headline}
                              className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                              loading="lazy"
                              onError={() => handleImageError(article.urlToImage)}
                            />
                          )}
                        </div>
                      </a>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <Newspaper size={32} className="text-gray-300 dark:text-gray-500 mx-auto mb-4" />
                      <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">No news available</h3>
                      <p className="text-gray-500 dark:text-gray-400">Check back later for the latest market updates</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Infinite Scroll Loader */}
        {activeTab === 'general' && hasMore && (
          <section>
            <div className="relative">
              <div ref={loader} className="flex justify-center p-4">
                <span className="text-gray-400 dark:text-gray-500">
                  {isFetchingMore ? 'Loading more...' : 'Scroll for more'}
                </span>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}